import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ConnectionType = "usb" | "wifi" | "bluetooth";

/** Normalize persisted settings — WiFi is deferred; fall back to USB. */
export function normalizeConnectionType(type: ConnectionType | undefined): ConnectionType {
  if (type === "bluetooth") return "bluetooth";
  return "usb";
}

export interface PrinterSettings {
  connectionType: ConnectionType;
  baudRate: number;
  dataBits: 7 | 8;
  stopBits: 1 | 2;
  parity: "none" | "even" | "odd";
  paperWidth: 58 | 80 | "A4";
  charset: "PC437" | "PC850" | "PC858";
  headerText: string;
  feedLines: number;
  density: 0 | 1 | 2;
  autoCut: boolean;
  autoConnect: boolean;
  /** Auto-send thermal receipt after sale completes (no Print click) */
  autoPrintOnSale: boolean;
  wifiHost: string;
  wifiPort: number;
  bluetoothDeviceId: string | null;
  bluetoothDeviceName: string | null;
  defaultPortId: string | null;
  /** Windows printer name via Sale Desk Print Helper (no COM port needed) */
  windowsPrinterName: string | null;
  usePrintHelper: boolean;
}

export interface SavedPort {
  id: string;
  label: string;
  usbVendorId?: number;
  usbProductId?: number;
}

export type ConnectionStatus = "idle" | "connecting" | "connected" | "error";

interface PrinterState {
  // Persisted settings
  settings: PrinterSettings;
  ports: SavedPort[];

  // Runtime (not persisted)
  status: ConnectionStatus;
  errorMsg: string | null;
  isDirty: boolean;
  draft: PrinterSettings; // in-progress edits before save

  // Settings actions
  updateDraft: <K extends keyof PrinterSettings>(key: K, value: PrinterSettings[K]) => void;
  saveDraft: () => void;
  discardDraft: () => void;
  resetDefaults: () => void;

  // Port actions
  setPorts: (ports: SavedPort[]) => void;
  addPort: (port: SavedPort) => void;

  // Connection runtime actions
  setStatus: (status: ConnectionStatus) => void;
  setError: (msg: string | null) => void;
}

export const DEFAULT_SETTINGS: PrinterSettings = {
  connectionType: "usb",
  baudRate: 9600,  // ← Standard thermal printer baud rate (was 115200)
  dataBits: 8,
  stopBits: 1,
  parity: "none",
  paperWidth: 80,
  charset: "PC437",
  headerText: "MY STORE",
  feedLines: 3,
  density: 1,
  autoCut: true,
  autoConnect: false,
  autoPrintOnSale: false,
  wifiHost: "192.168.1.100",
  wifiPort: 9100,
  bluetoothDeviceId: null,
  bluetoothDeviceName: null,
  defaultPortId: null,
  windowsPrinterName: null,
  usePrintHelper: true,
};

export const usePrinterStore = create<PrinterState>()(
  persist(
    (set) => ({
      // Persisted
      settings: DEFAULT_SETTINGS,
      ports: [],

      // Runtime
      status: "idle",
      errorMsg: null,
      isDirty: false,
      draft: DEFAULT_SETTINGS,

      updateDraft: (key, value) =>
        set((s) => ({ draft: { ...s.draft, [key]: value }, isDirty: true })),

      saveDraft: () =>
        set((s) => ({ settings: { ...s.draft }, isDirty: false })),

      discardDraft: () =>
        set((s) => ({ draft: { ...s.settings }, isDirty: false })),

      resetDefaults: () =>
        set({ draft: { ...DEFAULT_SETTINGS }, isDirty: true }),

      setPorts: (ports) => set({ ports }),

      addPort: (port) =>
        set((s) => ({
          ports: s.ports.find((p) => p.id === port.id)
            ? s.ports
            : [...s.ports, port],
        })),

      setStatus: (status) => set({ status }),
      setError: (errorMsg) => set({ errorMsg }),
    }),
    {
      name: "printer-settings", // localStorage key
      // Only persist settings and ports — not runtime state
      partialize: (s) => ({ settings: s.settings, ports: s.ports }),
      // Merge saved data with defaults so new fields never break old saves
      merge: (persisted: any, current) => {
        const raw = { ...DEFAULT_SETTINGS, ...(persisted?.settings || {}) };
        // Coerce unsupported paper widths and deferred WiFi mode
        const paperWidth = raw.paperWidth === 58 ? 58 : (raw.paperWidth === "A4" ? "A4" : 80);
        const connectionType = normalizeConnectionType(raw.connectionType);
        const settings = { ...raw, paperWidth, connectionType };
        return {
          ...current,
          settings,
          ports: persisted?.ports ?? [],
          draft: { ...settings },
        };
      },
    }
  )
);
